import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Layers, Eye, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import TemplatePreviewModal from '../components/TemplatePreviewModal';
import { openOdooEditorWithUrl } from '../lib/openOdooEditor';
import { fetchOdooSsoUrl, buildOdooTemplatesPath } from '../lib/odooSso';
import { formatApiError } from '../utils/apiError';
import TemplateGalleryPreview from '../components/TemplateGalleryPreview';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { categoryTheme } from '../utils/templatePreview';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';
const TEMPLATE_EDITOR = process.env.REACT_APP_TEMPLATE_EDITOR || 'native';
const ODOO_REDIRECT_STATUSES = new Set([502, 503, 504]);

const AIGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiTemplateLoading, setAiTemplateLoading] = useState(false);
  const [mode, setMode] = useState('template');
  const [templateView, setTemplateView] = useState('standard'); // 'standard' | 'ai-blueprints'
  const [formData, setFormData] = useState({
    name: searchParams.get('name') || '',
    description: '',
    aiPrompt: ''
  });
  const [genreAgents, setGenreAgents] = useState(null);
  const [createIndustryChatbot, setCreateIndustryChatbot] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, [selectedCategory]);

  useEffect(() => {
    const picked = templates.find((t) => t.id === selectedTemplate);
    if (!picked?.category) {
      setGenreAgents(null);
      return;
    }
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/ai-studio/templates/for-website-category/${encodeURIComponent(picked.category)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setGenreAgents(data))
      .catch(() => setGenreAgents(null));
  }, [selectedTemplate, templates]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/categories`);
      const data = await response.json();
      // Ensure categories is always an array to avoid runtime errors
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.categories)
          ? data.categories
          : [];
      setCategories(list);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchTemplates = async () => {
    try {
      const url = selectedCategory === 'all'
        ? `${API_URL}/api/templates`
        : `${API_URL}/api/templates?category=${selectedCategory}`;
      const response = await fetch(url);
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const handleAiTemplateGenerate = async () => {
    if (selectedCategory === 'all') {
      toast.error('Please select a specific category first');
      return;
    }

    try {
      setAiTemplateLoading(true);
      const token = localStorage.getItem('token');

      const categoryName =
        (Array.isArray(categories) ? categories : []).find((c) => c.id === selectedCategory)?.name ||
        selectedCategory;

      toast(`Generating an AI template for ${categoryName}...`);

      // Single request per click. For full-page, high-quality templates the LLM call
      // can be slow, so allow up to ~90 seconds before timing out.
      const REQUEST_TIMEOUT_MS = 90000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/ai-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category: selectedCategory,
          subcategory: null,
          style: 'modern-minimal',
          color_scheme: 'auto',
          features: [],
          pages: [],
          target_audience: '',
          // Still nudge the backend/LLM towards variety across clicks
          brand_personality: `variation-${Date.now()} for ${selectedCategory}`,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let data = null;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse AI template response JSON', e);
        data = null;
      }

      if (!res.ok || !data) {
        throw new Error((data && data.detail) || 'AI template generation failed');
      }

      // Prepend the newly generated template so it appears first
      setTemplates((prev) => [data, ...prev]);
      toast.success('Generated an AI template for this category');
    } catch (err) {
      console.error(err);
      if (err.name === 'AbortError') {
        toast.error('AI template generation took too long. Please try again.');
      } else {
        toast.error(err.message || 'Failed to generate AI template');
      }
    } finally {
      setAiTemplateLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template.id);
    if (template.category) {
      setSelectedCategory(template.category);
    }
    setPreviewTemplate(null);
  };

  const handleTemplatePreview = (e, template) => {
    e.stopPropagation();
    setPreviewTemplate(template);
  };

  const handleUseTemplate = async (template) => {
    setSelectedTemplate(template.id);
    setPreviewTemplate(null);
    toast.success(`${template.name} selected!`);
  };

  const handleGenerate = async () => {
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      toast.error('Please enter a website name');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setLoading(true);

    const createNativeWebsite = async (token) => {
      const picked = templates.find((t) => t.id === selectedTemplate);
      if (!picked) {
        throw new Error('Please re-select your template in the current category list.');
      }
      const response = await fetch(`${API_URL}/api/websites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          description: formData.description,
          template_id: selectedTemplate,
          category: picked.category,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error(formatApiError(payload, 'Something went wrong while creating the website.'));
        err.status = response.status;
        throw err;
      }
      return payload;
    };

    const createOdooWebsite = async (token) => {
      const response = await fetch(`${API_URL}/api/odoo/create-from-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          website_name: trimmedName,
          template_id: selectedTemplate,
          description: formData.description,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error(formatApiError(payload, 'Something went wrong while creating the website.'));
        err.status = response.status;
        throw err;
      }
      return payload;
    };

    const linkIndustryChatbot = async (token, websiteId, websiteName, category) => {
      if (!createIndustryChatbot || !category) return null;
      try {
        const bundleRes = await fetch(
          `${API_URL}/api/ai-studio/templates/for-website-category/${encodeURIComponent(category)}`,
        );
        if (!bundleRes.ok) return null;
        const bundle = await bundleRes.json();
        const primary = (bundle.templates || []).find((t) => t.is_primary) || bundle.templates?.[0];
        if (!primary?.id) return null;
        const linkRes = await fetch(`${API_URL}/api/ai-studio/apps/from-agent-template`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            template_id: primary.id,
            website_id: websiteId,
            website_name: websiteName,
            link_website_chatbot: true,
          }),
        });
        if (!linkRes.ok) return null;
        return linkRes.json();
      } catch (_) {
        return null;
      }
    };

    try {
      const token = localStorage.getItem('token');

      try {
        const checkRes = await fetch(`${API_URL}/api/websites`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (checkRes.ok) {
          const existingSites = await checkRes.json().catch(() => []);
          const nameTaken = Array.isArray(existingSites)
            && existingSites.some((site) => (site.name || '').trim().toLowerCase() === trimmedName.toLowerCase());

          if (nameTaken) {
            toast.error('This website name is already taken. Please choose a different name.');
            return;
          }
        }
      } catch (_) {
        // If the pre-check fails, continue and let the backend enforce uniqueness.
      }

      const openNativeEditor = async () => {
        const payload = await createNativeWebsite(token);
        const picked = templates.find((t) => t.id === selectedTemplate);
        const linked = await linkIndustryChatbot(token, payload.id, trimmedName, picked?.category);
        const category = (picked?.category || '').toLowerCase();
        const isEducation = category === 'education' || category === 'school' || category === 'university';
        if (linked?.website_linked) {
          toast.success(`Website created with ${linked.agent?.name || 'industry chatbot'} linked.`);
        } else if (isEducation) {
          toast.success('Website created — open Courses to Generate with AI.');
        } else {
          toast.success('Website created — opening the design editor.');
        }
        if (isEducation) {
          navigate(`/sites/${payload.id}/operate?section=courses`);
          return;
        }
        navigate(`/edit/${payload.id}`);
      };

      const openOdooEditor = async () => {
        let payload;
        try {
          payload = await createOdooWebsite(token);
        } catch (odooErr) {
          if (!ODOO_REDIRECT_STATUSES.has(odooErr.status)) {
            throw odooErr;
          }
          payload = {
            fallback: true,
            templates_path: buildOdooTemplatesPath({
              templateId: selectedTemplate,
              websiteName: trimmedName,
            }),
          };
        }

        const redirectPath = payload.templates_path || buildOdooTemplatesPath({
          templateId: selectedTemplate,
          websiteName: trimmedName,
          websiteId: payload.odoo_website_id,
        });

        let sso;
        try {
          sso = await fetchOdooSsoUrl(redirectPath, token, API_URL);
        } catch (ssoErr) {
          toast.error(ssoErr.message || 'Could not open Odoo editor.');
          return;
        }

        if (payload.template_applied) {
          toast.success('Opening Odoo editor with your selected template…');
        } else if (sso.method === 'bridge_auto_login') {
          toast.success('Opening Odoo editor…');
        } else if (sso.method === 'form_login') {
          toast('Signing you into Odoo…', { duration: 4000 });
        } else {
          toast('Odoo SSO is not configured — you may need to sign in manually.', { duration: 7000 });
        }

        openOdooEditorWithUrl(async () => sso.url);
        navigate('/dashboard');
      };

      if (TEMPLATE_EDITOR === 'native') {
        await openNativeEditor();
        return;
      }

      await openOdooEditor();
    } catch (error) {
      if (error?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error(error.message || 'Something went wrong while creating the website.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="ai-generator-page">
      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelect={handleUseTemplate}
        />
      )}
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-2" data-testid="generator-title">
            Create from Template
          </h1>
          <p className="text-zinc-400" data-testid="generator-subtitle">
            Start from 125+ professionally designed templates — customise and publish in minutes.
          </p>
          {TEMPLATE_EDITOR === 'odoo' && (
            <p className="text-xs text-zinc-500 mt-2">
              Odoo editor mode — requires ODOO_BRIDGE_TOKEN on the backend.
            </p>
          )}
          {TEMPLATE_EDITOR === 'native' && (
            <p className="text-xs text-zinc-500 mt-2">
              Opens in the Snagcart editor with genre-specific admin and user dashboards.
            </p>
          )}
        </div>

        <ModuleDemoVideo moduleKey="create-from-template" className="mb-4" />

        {/* Form */}
        <div className="glass p-8 rounded-2xl space-y-6" data-testid="template-generator-form">
          <div>
            <Label htmlFor="name" className="text-white mb-2 block">Website Name *</Label>
            <Input
              id="name"
              placeholder="My Awesome Website"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="website-name-input"
              className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-white mb-2 block">Description (Optional)</Label>
            <Input
              id="description"
              placeholder="A brief description of your website"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              data-testid="website-description-input"
              className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
            />
          </div>

          <div>
              <Label className="text-white mb-2 block">Select Template *</Label>

              {/* View Toggle: Standard vs AI Blueprints */}
              <div className="flex items-center gap-2 mb-4 text-xs text-zinc-400">
                <span>View:</span>
                <button
                  type="button"
                  onClick={() => setTemplateView('standard')}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-all ${
                    templateView === 'standard'
                      ? 'bg-zinc-900 border-violet-500 text-violet-300'
                      : 'bg-zinc-900/40 border-zinc-700 text-zinc-400 hover:text-white'
                  }`}
                >
                  Standard Templates
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateView('ai-blueprints')}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition-all ${
                    templateView === 'ai-blueprints'
                      ? 'bg-zinc-900 border-emerald-500 text-emerald-300'
                      : 'bg-zinc-900/40 border-zinc-700 text-zinc-400 hover:text-white'
                  }`}
                >
                  AI Blueprints (Experimental)
                </button>
              </div>
              
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => { setSelectedCategory('all'); setSelectedTemplate(null); }}
                  data-testid="category-all"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-violet-500 text-white'
                      : 'bg-zinc-900/50 text-zinc-400 hover:text-white'
                  }`}
                >
                  All Templates
                </button>
                {(Array.isArray(categories) ? categories : []).map((category) => {
                  const catTheme = categoryTheme(category.id);
                  const isActive = selectedCategory === category.id;
                  return (
                  <button
                    key={category.id}
                    onClick={() => { setSelectedCategory(category.id); setSelectedTemplate(null); }}
                    data-testid={`category-${category.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 border ${
                      isActive
                        ? 'text-white shadow-md'
                        : 'bg-zinc-900/50 text-zinc-400 hover:text-white hover:bg-zinc-800 border-transparent'
                    }`}
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${catTheme.accent}dd, ${catTheme.accent}99)`,
                      borderColor: catTheme.accent,
                    } : undefined}
                  >
                    {category.icon && <span>{category.icon}</span>}
                    {category.name}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-black/20 text-white' : 'bg-zinc-700 text-zinc-300'
                    }`}>{templateView === 'ai-blueprints' ? (category.ai_count ?? 0) : category.count}</span>
                  </button>
                );})}
              </div>

              {/* AI Template Generation Button (only for AI Blueprints view) */}
              {selectedCategory !== 'all' && templateView === 'ai-blueprints' && (
                <div className="mb-6">
                  <Button
                    onClick={handleAiTemplateGenerate}
                    disabled={aiTemplateLoading}
                    data-testid="ai-template-generate-button"
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white rounded-xl h-12 font-bold transition-all"
                  >
                    {aiTemplateLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Generating AI Template...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate AI Template for {categories.find(c => c.id === selectedCategory)?.name}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Template Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[28rem] overflow-y-auto">
                {(() => {
                  if (templateView === 'ai-blueprints') {
                    const v2Templates = templates.filter((template) => template.source === 'ai-blueprint-v2');

                    // If we already have v2 AI blueprints, show only those.
                    if (v2Templates.length > 0) {
                      return v2Templates;
                    }

                    // Fallback: show whatever templates are available (legacy/static)
                    // so the grid is never empty for the user.
                    return templates;
                  }

                  // Standard view: built-in templates only (matches category badge counts).
                  return templates.filter((t) => !String(t.source || '').startsWith('ai'));
                })().map((template) => {
                  const theme = categoryTheme(template.category);
                  const isSelected = selectedTemplate === template.id;
                  return (
                  <div
                    key={template.id}
                    data-testid={`template-card-${template.id}`}
                    className={`relative text-left p-3 rounded-xl transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-zinc-900/80 border-2 ring-2'
                        : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-600'
                    }`}
                    style={isSelected ? {
                      borderColor: theme.accent,
                      boxShadow: `0 0 0 1px ${theme.accent}44, 0 8px 24px ${theme.accent}22`,
                    } : undefined}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    {isSelected && (
                      <div
                        className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: theme.accent }}
                      >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    <div className="relative group">
                      <TemplateGalleryPreview template={template} />
                      <button
                        onClick={(e) => handleTemplatePreview(e, template)}
                        data-testid={`preview-template-${template.id}`}
                        className="absolute bottom-3 right-1 bg-white/90 hover:bg-white text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                      >
                        <Eye className="w-3 h-3" />
                        Preview
                      </button>
                    </div>
                    <h4 className="font-bold text-sm mb-1" data-testid={`template-name-${template.id}`}>{template.name}</h4>
                    <p className="text-xs text-zinc-400 mb-2 line-clamp-2" data-testid={`template-desc-${template.id}`}>{template.description}</p>
                    <div className="flex items-center justify-between text-[11px] mt-1">
                      <span className="capitalize font-medium" style={{ color: theme.accent }}>{template.category?.replace('_', ' ')}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide ${
                          String(template.id || '').startsWith('external-')
                            ? 'border-amber-400 text-amber-300 bg-amber-500/10'
                            : String(template.source || '').startsWith('ai')
                              ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                              : 'border-zinc-600 text-zinc-400 bg-zinc-900/60'
                        }`}
                      >
                        {String(template.id || '').startsWith('external-')
                          ? 'Imported'
                          : String(template.source || '').startsWith('ai')
                            ? 'AI-generated'
                            : 'Built-in'}
                      </span>
                    </div>
                  </div>
                );})}
              </div>
            </div>

          {genreAgents?.templates?.length > 0 && (
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-white">
                    {genreAgents.icon} Industry AI assistant
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Matched to <span className="text-violet-300">{genreAgents.label}</span> templates — powers your site chat widget.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-zinc-300 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createIndustryChatbot}
                    onChange={(e) => setCreateIndustryChatbot(e.target.checked)}
                    className="rounded border-white/20"
                  />
                  Auto-create
                </label>
              </div>
              <div className="text-xs text-zinc-400">
                Primary:{' '}
                <span className="text-white font-medium">
                  {(genreAgents.templates.find((t) => t.is_primary) || genreAgents.templates[0])?.name}
                </span>
                {genreAgents.templates.length > 1 && (
                  <span className="text-zinc-500">
                    {' '}
                    (+{genreAgents.templates.length - 1} more in AI Studio Templates)
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              onClick={handleGenerate}
              disabled={loading}
              data-testid="generate-button"
              className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-full h-12 font-bold transition-transform hover:scale-105"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                  {mode === 'ai' ? 'Generating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {mode === 'ai' ? 'Generate with AI' : 'Create from Template'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIGenerator;